import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Trash2, X, MessageSquare, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { formatDateID } from "@/lib/format";

const STATUS_BADGE = {
  pending: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  approved: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  rejected: "border-rose-500/40 bg-rose-500/10 text-rose-400",
};
const STATUS_LABEL = { pending: "Menunggu", approved: "Disetujui", rejected: "Ditolak" };

export function getWaUrl(phone, name) {
  if (!phone) return "#";
  let clean = phone.replace(/[^0-9]/g, "");
  if (clean.startsWith("0")) {
    clean = "62" + clean.slice(1);
  }
  const text = encodeURIComponent(`Halo ${name}, kami dari pengurus Captiva Chevy Club (3C) Chapter Jawa Timur...`);
  return `https://wa.me/${clean}?text=${text}`;
}

export const RegistrationsTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Semua");

  const load = () => api.get("/admin/registrations").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (reg, status) => {
    try {
      await api.patch(`/admin/registrations/${reg.id}`, { status });
      toast.success(`Pendaftaran ${reg.name} ${status === "approved" ? "disetujui" : "ditolak"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (reg) => {
    if (!window.confirm(`Hapus pendaftaran "${reg.name}"?`)) return;
    await api.delete(`/admin/registrations/${reg.id}`);
    toast.success("Pendaftaran dihapus");
    load();
  };

  const filtered = items.filter((r) => filter === "Semua" || r.status === filter);

  return (
    <div className="space-y-4" data-testid="admin-registrations-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-2xl font-bold uppercase text-white">Pendaftaran Anggota</h2>
        <div className="flex gap-2">
          {[["Semua", "Semua"], ["pending", "Menunggu"], ["approved", "Disetujui"], ["rejected", "Ditolak"]].map(([val, label]) => (
            <button key={val} data-testid={`reg-filter-${val}`} onClick={() => setFilter(val)}
              className={`rounded-md border px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                filter === val ? "border-rose-500/50 bg-rose-500/15 text-rose-400" : "border-white/10 text-slate-400 hover:text-slate-200"
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10 bg-[#12141C]/90">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-400">Nama</TableHead>
              <TableHead className="text-slate-400">WhatsApp / Kontak</TableHead>
              <TableHead className="text-slate-400">Kota</TableHead>
              <TableHead className="text-slate-400">Captiva</TableHead>
              <TableHead className="text-slate-400">Tanggal</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-right text-slate-400">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`admin-reg-row-${r.id}`} className="border-white/10">
                <TableCell className="font-medium text-slate-200">
                  {r.name}
                  {r.message && <p className="mt-1 max-w-[220px] truncate text-xs font-normal text-slate-500" title={r.message}>{r.message}</p>}
                </TableCell>
                <TableCell className="text-slate-400">
                  <a
                    href={getWaUrl(r.phone, r.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-medium text-emerald-400 transition-colors hover:text-emerald-300 hover:underline"
                    title={`Klik untuk Chat WhatsApp ke ${r.phone}`}
                  >
                    <MessageSquare className="h-3.5 w-3.5 text-emerald-400" />
                    {r.phone}
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </a>
                </TableCell>
                <TableCell className="text-slate-400">{r.city || "-"}</TableCell>
                <TableCell className="text-slate-400">
                  {[r.car_year, r.car_color, r.plate_number].filter(Boolean).join(" • ") || "-"}
                </TableCell>
                <TableCell className="text-slate-400">{formatDateID(r.created_at)}</TableCell>
                <TableCell>
                  <span data-testid={`reg-status-${r.id}`} className={`rounded-md border px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest ${STATUS_BADGE[r.status] || STATUS_BADGE.pending}`}>
                    {STATUS_LABEL[r.status] || r.status}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {r.status !== "approved" && (
                    <Button data-testid={`reg-approve-${r.id}`} size="icon" variant="ghost" title="Setujui"
                      onClick={() => setStatus(r, "approved")} className="text-slate-400 hover:text-emerald-400">
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                  {r.status !== "rejected" && (
                    <Button data-testid={`reg-reject-${r.id}`} size="icon" variant="ghost" title="Tolak"
                      onClick={() => setStatus(r, "rejected")} className="text-slate-400 hover:text-amber-400">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  <Button data-testid={`reg-delete-${r.id}`} size="icon" variant="ghost" title="Hapus"
                    onClick={() => remove(r)} className="text-slate-400 hover:text-rose-400">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow className="border-white/10">
                <TableCell colSpan={7} data-testid="reg-empty-state" className="py-8 text-center text-slate-500">
                  Belum ada pendaftaran pada status ini.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
